/// <reference path="../../../typings/expr-eval.d.ts" />
import { Injectable } from '@angular/core';
import { ColumnType } from './self-service-types';

/**
 * Pure expression / column primitives shared by the self-service feature.
 *
 * Extracted verbatim from self-service-visualization.component.ts. Everything
 * here is stateless: string/value in, value/function out. Stateful validators
 * (which read the component's dataset / formula list) stay in their owning
 * component and delegate the pure parts to this service.
 */
@Injectable({
  providedIn: 'root',
})
export class SelfServiceFormulaService {
  private _formulajs: any | null = null;

  async ensureFormulajs(): Promise<void> {
    if (this._formulajs) return;
    try {
      // dynamic import so builds that don't install the optional dep still work
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const mod = await import('expr-eval');
      this._formulajs = mod; // reuse this field for a generic parser module
    } catch {
      this._formulajs = null;
    }
  }
  /**
   * Replace `[Column Name]` bracket references and bare column identifiers in an
   * expression using the supplied replacer. Longer names are substituted first
   * so that e.g. `Revenue` does not clobber `RevenueGrowth`.
   */
  substituteColumnRefs(
    expression: string,
    columnNames: string[],
    replacer: (name: string) => string,
  ): string {
    let result = expression.replace(/\[([^\]]+)\]/g, (_, name) => replacer(name.trim()));
    const sortedNames = [...columnNames].sort((a, b) => b.length - a.length);
    for (const name of sortedNames) {
      if (/[^A-Za-z0-9_]/.test(name)) continue;
      const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const pattern = new RegExp(`(?<![\\w'"\\$])${escaped}(?![\\w'"(])`, 'g');
      result = result.replace(pattern, replacer(name));
    }
    return result;
  }

  /** Normalize SQL-ish AND/OR/NOT keywords to JS boolean operators. */
  normalizeLogicalOps(expression: string): string {
    return expression.replace(/\bAND\b/gi, '&&').replace(/\bOR\b/gi, '||').replace(/\bNOT\b/gi, '!');
  }

  /** Build a per-row evaluator function from a computed-column expression. */
  buildRowEvaluator(
    expression: string,
    columnNames: string[],
  ): (row: Record<string, string>) => unknown {
    // Try expr-eval parser first when available
    try {
      const Parser = this._formulajs?.Parser;
      if (Parser) {
        const varMap: Record<string, string> = {};
        columnNames.forEach((name, i) => { varMap[name] = `c${i}`; });
        const substituted = this.substituteColumnRefs(expression, columnNames, (name) => varMap[name]);
        const normalized = this.normalizeLogicalOps(substituted);
        const parsed = Parser.parse(normalized);
        return (row: Record<string, string>) => {
          const vars: any = {};
          columnNames.forEach((name) => {
            const raw = row[name];
            const num = Number(String(raw ?? '').replace(/,/g, '').trim());
            vars[varMap[name]] = Number.isFinite(num) ? num : (raw ?? '');
          });
          vars.SUM = (...args: any[]) => args.flat().reduce((s: number, v: any) => s + (Number(v) || 0), 0);
          vars.AVERAGE = (...args: any[]) => { const a = args.flat().map((v: any) => Number(v) || 0); return a.length ? a.reduce((s, x) => s + x, 0) / a.length : 0; };
          vars.weightedAverage = (vals: any[], weights: any[]) => { if (!Array.isArray(vals)) return 0; if (!Array.isArray(weights)) return 0; const n = Math.min(vals.length, weights.length); let num = 0, den = 0; for (let i = 0; i < n; i++) { const v = Number(vals[i]) || 0; const w = Number(weights[i]) || 0; num += v * w; den += w; } return den ? num / den : 0; };
          vars.IF = (c: any, t: any, e: any) => (c ? t : e);
          vars.concat = (...a: any[]) => a.map((x) => String(x ?? '')).join('');
          vars.CONCATENATE = vars.concat;
          vars.IFERROR = (val: any, fallback: any) => { try { return val; } catch { return fallback; } };
          return parsed.evaluate(vars);
        };
      }
    } catch {
      // ignore; fall back to Function-based evaluator below
    }

    const substituted = this.substituteColumnRefs(expression, columnNames, (name) => `__num(__row[${JSON.stringify(name)}])`);
    const normalized = this.normalizeLogicalOps(substituted);
    const body = `
      const __num = (v) => { const s = String(v ?? '').replace(/,/g, '').trim(); const n = Number(s); return Number.isFinite(n) ? n : (s === '' ? 0 : s); };
      const IF = (c, t, e) => (c ? t : e);
      // Excel-like aliases
      const SUM = (...args) => args.flat().reduce((acc, v) => acc + (Number(v) || 0), 0);
      const AVERAGE = (...args) => {
        const a = args.flat();
        return a.length ? SUM(a) / a.length : 0;
      };
      const COUNT = (...args) => args.flat().filter((v) => v !== null && v !== undefined && v !== '').length;
      const COUNTIF = (a, criteria) => {
        if (!Array.isArray(a)) return 0;
        if (criteria == null) return a.length;
        const s = String(criteria).trim();
        const opMatch = s.match(/^(<=|>=|<>|<|>|=)/);
        if (opMatch) {
          const op = opMatch[1];
          const rhs = Number(s.substring(op.length));
          if (Number.isFinite(rhs)) {
            return a.filter((v) => {
              const n = Number(v);
              if (!Number.isFinite(n)) return false;
              return op === '<' ? n < rhs : op === '>' ? n > rhs : op === '<=' ? n <= rhs : op === '>=' ? n >= rhs : op === '=' ? n === rhs : op === '<>' ? n !== rhs : false;
            }).length;
          }
        }
        // fallback: substring/equality match
        return a.filter((v) => String(v).includes(String(criteria))).length;
      };
      const IFERROR = (expr, fallback) => {
        try { return expr; } catch { return fallback; }
      };
      const concat = (...a) => a.map((x) => String(x ?? '')).join('');
      const CONCATENATE = concat;
      const upper = (s) => String(s ?? '').toUpperCase();
      const lower = (s) => String(s ?? '').toLowerCase();
      const trim = (s) => String(s ?? '').trim();
      const length = (s) => String(s ?? '').length;
      const substr = (s, start, end) => String(s ?? '').substring(start, end);
      const replace = (s, find, repl) => String(s ?? '').split(String(find)).join(String(repl));
      const contains = (s, sub) => String(s ?? '').includes(String(sub));
      const startsWith = (s, p) => String(s ?? '').startsWith(String(p));
      const endsWith = (s, p) => String(s ?? '').endsWith(String(p));
      const year = (d) => { const x = new Date(d); return Number.isNaN(x.getTime()) ? 0 : x.getFullYear(); };
      const month = (d) => { const x = new Date(d); return Number.isNaN(x.getTime()) ? 0 : x.getMonth() + 1; };
      const day = (d) => { const x = new Date(d); return Number.isNaN(x.getTime()) ? 0 : x.getDate(); };
      const dayOfWeek = (d) => { const x = new Date(d); return Number.isNaN(x.getTime()) ? 0 : x.getDay(); };
      const abs = Math.abs, round = Math.round, ceil = Math.ceil, floor = Math.floor;
      const sqrt = Math.sqrt, log = Math.log, exp = Math.exp, pow = Math.pow;
      const min = (...a) => Math.min(...a), max = (...a) => Math.max(...a);
      const VARP = (a) => {
        if (!a.length) return 0; const m = avg(a); return a.reduce((s,v)=>s+Math.pow(v-m,2),0)/a.length;
      };
      const VARS = (a) => {
        if (!a.length) return 0; const m = avg(a); return a.length>1 ? a.reduce((s,v)=>s+Math.pow(v-m,2),0)/(a.length-1) : 0;
      };
      const STDEVP = (a) => Math.sqrt(VARP(a));
      const STDEVS = (a) => Math.sqrt(VARS(a));
      const weightedAverage = (vals, weights) => {
        if (!Array.isArray(vals)) return 0;
        if (!Array.isArray(weights)) return avg(vals);
        const n = Math.min(vals.length, weights.length);
        let num = 0, den = 0;
        for (let i = 0; i < n; i++) { const v = Number(vals[i]) || 0; const w = Number(weights[i]) || 0; num += v * w; den += w; }
        return den ? num / den : 0;
      };
      return (${normalized});
    `;
    // eslint-disable-next-line @typescript-eslint/no-implied-eval
    return new Function('__row', body) as (row: Record<string, string>) => unknown;
  }

  /** Build a per-group evaluator function from a custom-aggregation expression. */
  buildAggregationEvaluator(
    expression: string,
    columnNames: string[],
  ): (rows: Record<string, string>[]) => number {
    const substituted = this.substituteColumnRefs(expression, columnNames, (name) => `__col(${JSON.stringify(name)})`);
    // Try expr-eval parser first
    try {
      const Parser = this._formulajs?.Parser;
      if (Parser) {
        const varMap: Record<string, string> = {};
        columnNames.forEach((name, i) => { varMap[name] = `c${i}`; });
        const substituted = this.substituteColumnRefs(expression, columnNames, (name) => varMap[name]);
        const normalized = this.normalizeLogicalOps(substituted);
        const parsed = Parser.parse(normalized);
        return (rows: Record<string, string>[]) => {
          const vars: any = {};
          columnNames.forEach((name, i) => {
            vars[varMap[name]] = rows.map((r) => {
              const raw = r[name]; const n = Number(String(raw ?? '').replace(/,/g, '').trim()); return Number.isFinite(n) ? n : 0;
            });
          });
          vars.SUM = (...args: any[]) => args.flat().reduce((s: number, v: any) => s + (Number(v) || 0), 0);
          vars.AVERAGE = (...args: any[]) => { const a = args.flat().map((v: any) => Number(v) || 0); return a.length ? a.reduce((s, x) => s + x, 0) / a.length : 0; };
          vars.weightedAverage = (vals: any[], weights: any[]) => { if (!Array.isArray(vals)) return 0; if (!Array.isArray(weights)) return 0; const n = Math.min(vals.length, weights.length); let num = 0, den = 0; for (let i = 0; i < n; i++) { const v = Number(vals[i]) || 0; const w = Number(weights[i]) || 0; num += v * w; den += w; } return den ? num / den : 0; };
          vars.IF = (c: any, t: any, e: any) => (c ? t : e);
          vars.concat = (...a: any[]) => a.map((x) => String(x ?? '')).join('');
          vars.CONCATENATE = vars.concat;
          vars.IFERROR = (val: any, fallback: any) => { try { return val; } catch { return fallback; } };
          const result = parsed.evaluate(vars);
          return Number(result) || 0;
        };
      }
    } catch {
      // fall through to Function fallback
    }

    const normalized = this.normalizeLogicalOps(substituted);
    const body = `
      const __num = (v) => { const s = String(v ?? '').replace(/,/g, '').trim(); const n = Number(s); return Number.isFinite(n) ? n : 0; };
      const __col = (name) => __rows.map((r) => __num(r[name]));
      const sum = (a) => a.reduce((acc, v) => acc + (typeof v === 'number' ? v : 0), 0);
      const SUM = (...args) => sum(args.flat());
      const avg = (a) => a.length ? sum(a) / a.length : 0;
      const AVERAGE = (...args) => { const a = args.flat(); return a.length ? sum(a) / a.length : 0; };
      const count = (a) => Array.isArray(a) ? a.length : __rows.length;
      const distinctCount = (a) => new Set(a).size;
      const min = (a) => a.length ? Math.min(...a) : 0;
      const max = (a) => a.length ? Math.max(...a) : 0;
      const VARP = (a) => { if (!a.length) return 0; const m = avg(a); return a.reduce((s,v)=>s+Math.pow(v-m,2),0)/a.length; };
      const VARS = (a) => { if (!a.length) return 0; const m = avg(a); return a.length>1 ? a.reduce((s,v)=>s+Math.pow(v-m,2),0)/(a.length-1) : 0; };
      const STDEVP = (a) => Math.sqrt(VARP(a));
      const STDEVS = (a) => Math.sqrt(VARS(a));
      const weightedAverage = (vals, weights) => {
        if (!Array.isArray(vals)) return 0;
        if (!Array.isArray(weights)) return avg(vals);
        const n = Math.min(vals.length, weights.length);
        let num = 0, den = 0;
        for (let i = 0; i < n; i++) { const v = Number(vals[i]) || 0; const w = Number(weights[i]) || 0; num += v * w; den += w; }
        return den ? num / den : 0;
      };
      const median = (a) => percentile(a, 50);
      const percentile = (a, p) => {
        if (!a.length) return 0;
        const s = [...a].sort((x, y) => x - y);
        const rank = (p / 100) * (s.length - 1);
        const lo = Math.floor(rank), hi = Math.ceil(rank);
        return lo === hi ? s[lo] : s[lo] + (s[hi] - s[lo]) * (rank - lo);
      };
      const first = (a) => a[0];
      const last = (a) => a[a.length - 1];
      const range = (a) => max(a) - min(a);
      const abs = Math.abs, round = Math.round, ceil = Math.ceil, floor = Math.floor;
      const sqrt = Math.sqrt, log = Math.log, exp = Math.exp, pow = Math.pow;
      const IF = (c, t, e) => (c ? t : e);
      return Number(${normalized});
    `;
    // eslint-disable-next-line @typescript-eslint/no-implied-eval
    return new Function('__rows', body) as (rows: Record<string, string>[]) => number;
  }

  // ---- Helper utilities used by the data-builder component ----
  isEntityNoValueOperator(operator: string | null | undefined): boolean {
    if (!operator) return false;
    const noop = operator.toUpperCase().trim();
    return noop === 'IS NULL' || noop === 'IS NOT NULL';
  }

  parseEntityFilterValues(operator: string | null | undefined, value: string): string[] {
    if (!operator) return [value];
    const op = operator.toUpperCase().trim();
    if (op === 'IS NULL' || op === 'IS NOT NULL') return [];
    if (op === 'IN' || op === 'NOT IN') {
      return value.split(',').map((v: string) => v.trim()).filter((v: string) => v !== '');
    }
    if (op === 'BETWEEN') {
      return value.split(',').map((v: string) => v.trim()).slice(0, 2);
    }
    return [value.trim()];
  }

  inferColumnType(values: string[]): ColumnType {
    const nonEmpty = values.filter((v) => v != null && String(v).trim() !== '');
    if (!nonEmpty.length) return 'empty';
    const allBoolean = nonEmpty.every((v) => /^(true|false)$/i.test(String(v).trim()));
    if (allBoolean) return 'boolean';
    const allNumber = nonEmpty.every((v) => { const n = Number(String(v).replace(/,/g, '').trim()); return Number.isFinite(n); });
    if (allNumber) return 'number';
    const allDate = nonEmpty.every((v) => { const d = new Date(String(v)); return !Number.isNaN(d.getTime()); });
    if (allDate) return 'date';
    return 'string';
  }

  hasBalancedParentheses(expression: string): boolean {
    let depth = 0;
    for (const ch of expression) {
      if (ch === '(') depth += 1;
      if (ch === ')') depth -= 1;
      if (depth < 0) return false;
    }
    return depth === 0;
  }
}